import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  SUBTITLE_COLOR,
  SUBTITLE_FONT_FAMILY,
  SUBTITLE_FONT_WEIGHT,
  SUBTITLE_LINE_HEIGHT,
  SUBTITLE_TEXT_SHADOW,
  calcSubtitleFontSize,
} from '~/styles/subtitleStyle'
import { useFontSizeTier } from './useFontSizeTier'
import { useFullscreen } from './useFullscreen'
import { useLang } from './useLang'
import { useVideoScale } from './useVideoScale'

type Props = {
  video: HTMLVideoElement
  showedControls: boolean
}

const SEEK_BAR_HEIGHT = 40
const MENU_VERTICAL_PADDING = 24
const MENU_CONTROL_HEIGHT = 52

const SUBTITLE_BOTTOM_OFFSET_WITH_CONTROLS =
  SEEK_BAR_HEIGHT + MENU_VERTICAL_PADDING * 2 + MENU_CONTROL_HEIGHT
const SUBTITLE_BOTTOM_OFFSET_WITHOUT_CONTROLS =
  SEEK_BAR_HEIGHT + MENU_VERTICAL_PADDING
const FONT_PRELOAD_SECONDS = 3

const normalizeCueText = (text: string) =>
  text.replace(/<[^>]*>/g, '').replace(/\s+/g, '')

export const Subtitle = ({ video, showedControls }: Props) => {
  const { videoScale, videoWidth } = useVideoScale()
  const { fullscreen } = useFullscreen()
  const { lang } = useLang()
  const [nativeSubtitle, setNativeSubtitle] = useState('')
  const { sizeTier } = useFontSizeTier()
  const preloadedFontTextsRef = useRef<Set<string>>(new Set())
  const lastPreloadedSubtitleRef = useRef({ lang: '', second: -1 })

  const fontSize = useMemo(
    () => calcSubtitleFontSize(videoWidth, fullscreen, sizeTier),
    [videoWidth, fullscreen, sizeTier],
  )

  const preloadNotoSansJpFont = useCallback(
    (text: string) => {
      const normalizedText = normalizeCueText(text)
      if (!normalizedText || preloadedFontTextsRef.current.has(normalizedText)) {
        return
      }
      if (typeof document === 'undefined' || !document.fonts) {
        return
      }

      preloadedFontTextsRef.current.add(normalizedText)
      document.fonts
        .load(`${SUBTITLE_FONT_WEIGHT} ${fontSize}px "Noto Sans JP"`, normalizedText)
        .catch(() => {})
    },
    [fontSize],
  )

  useEffect(() => {
    const tracks = video.textTracks
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i]
      track.mode = 'hidden'
    }
  }, [video])

  useEffect(() => {
    let rafId: number

    const updateNativeSubtitle = () => {
      if (!lang) {
        setNativeSubtitle('')
        return
      }

      const tracks = video.textTracks

      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i]
        if (track.language === lang && track.activeCues) {
          const cue = track.activeCues[0] as VTTCue
          setNativeSubtitle((cue?.text ?? '').replace(/\n/g, '<br>'))
        }
      }

      rafId = requestAnimationFrame(updateNativeSubtitle)
    }

    rafId = requestAnimationFrame(updateNativeSubtitle)
    video.addEventListener('seeked', updateNativeSubtitle)
    return () => {
      video.removeEventListener('seeked', updateNativeSubtitle)
      cancelAnimationFrame(rafId)
    }
  }, [video, lang])

  useEffect(() => {
    if (!lang) {
      return
    }

    const preloadUpcomingNotoSansJpFonts = () => {
      const currentSecond = Math.floor(video.currentTime)
      const shouldPreloadUpcomingCues =
        lastPreloadedSubtitleRef.current.lang !== lang ||
        lastPreloadedSubtitleRef.current.second !== currentSecond

      if (!shouldPreloadUpcomingCues) {
        return
      }

      const tracks = video.textTracks

      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i]
        if (track.language !== lang) {
          continue
        }

        const cues = track.cues
        if (!cues) {
          return
        }

        lastPreloadedSubtitleRef.current = { lang, second: currentSecond }

        const preloadEndTime = video.currentTime + FONT_PRELOAD_SECONDS
        for (let j = 0; j < cues.length; j++) {
          const upcomingCue = cues[j] as VTTCue
          if (
            upcomingCue.startTime >= video.currentTime &&
            upcomingCue.startTime <= preloadEndTime
          ) {
            preloadNotoSansJpFont(upcomingCue.text)
          }
        }
      }
    }

    preloadUpcomingNotoSansJpFonts()
    video.addEventListener('timeupdate', preloadUpcomingNotoSansJpFonts)
    video.addEventListener('seeked', preloadUpcomingNotoSansJpFonts)

    return () => {
      video.removeEventListener('timeupdate', preloadUpcomingNotoSansJpFonts)
      video.removeEventListener('seeked', preloadUpcomingNotoSansJpFonts)
    }
  }, [video, lang, preloadNotoSansJpFont])

  const bottomOffset = useMemo(
    () =>
      videoScale *
      (showedControls
        ? SUBTITLE_BOTTOM_OFFSET_WITH_CONTROLS
        : SUBTITLE_BOTTOM_OFFSET_WITHOUT_CONTROLS),
    [showedControls, videoScale],
  )

  return (
    <div
      style={{
        position: 'absolute',
        bottom: bottomOffset + 'px',
        padding: '8px',
        transition: 'all 0.2s cubic-bezier(0.3, 0.3, 0.3, 1)',
      }}
    >
      {lang && nativeSubtitle && (
        <span
          style={{
            fontSize: `${fontSize}px`,
            fontFamily: SUBTITLE_FONT_FAMILY,
            fontWeight: SUBTITLE_FONT_WEIGHT,
            textShadow: SUBTITLE_TEXT_SHADOW,
            lineHeight: SUBTITLE_LINE_HEIGHT,
            color: SUBTITLE_COLOR,
            pointerEvents: 'none',
          }}
          dangerouslySetInnerHTML={{ __html: nativeSubtitle }}
        />
      )}
    </div>
  )
}
